import Grid from '@mui/material/Grid';
import { ListIconType } from '@openx/types/targeting/targeting';
import { type ComparisonMap, Criteria, type OptionEntriesProps } from '@openx/types/targeting/targetingValuesTypes';
import {
  type TechnologyAndDevicesFormState,
  TechnologyAndDevicesOption,
} from '@openx/types/targeting/technologyAndDevices';
import { noop } from '@openx/utils/noop';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { mapTypeToIconType, useComparisonLabels } from '../../constants';
import { DimensionChip } from '../../shared/DimensionChip/DimensionChip';
import { FiltersTitle } from '../../shared/FiltersTitle/FiltersTitle';
import { SelectedItems } from '../../shared/SelectedItems/SelectedItems';
import { Switch } from '../../shared/Switch/Switch';
import type { TargetingItemsProps } from '../../types';

export const UserAgentItems = ({
  targetingParams,
  readonly = true,
  onChange = noop,
  disabledReasonMessage,
}: TargetingItemsProps<TechnologyAndDevicesFormState>) => {
  const { t } = useTranslation();
  const comparisonLabels = useComparisonLabels();

  const { op: dimension, val: optionsMap } = targetingParams[TechnologyAndDevicesOption.USER_AGENT];
  const switchState = dimension === Criteria.ANY;
  const optionsEntries: Array<OptionEntriesProps> = useMemo(() => Object.entries(optionsMap), [optionsMap]);
  const selectedItemsCount = useMemo(() => optionsEntries.flatMap(option => option[1]).length, [optionsEntries]);

  const handleChange = useCallback(
    (val: string[], comparisonType?: string) => {
      let newState: ComparisonMap = { ...optionsMap };
      const isRemovingGroup = !val.length && comparisonType;
      const isRemovingSingleOption = val.length && comparisonType;
      const isRemovingAll = !val.length && !comparisonType;

      if (isRemovingGroup) {
        delete newState[comparisonType];
      }

      if (isRemovingSingleOption) {
        newState[comparisonType] = val;
      }

      if (isRemovingAll) {
        newState = {};
      }

      onChange(state => ({
        ...state,
        [TechnologyAndDevicesOption.USER_AGENT]: {
          op: dimension,
          val: newState,
        },
      }));
    },
    [onChange, dimension, optionsMap]
  );

  const onSwitchChange = useCallback(() => {
    onChange(state => ({
      ...state,
      [TechnologyAndDevicesOption.USER_AGENT]: { op: switchState ? Criteria.ALL : Criteria.ANY, val: optionsMap },
    }));
  }, [onChange, switchState, optionsMap]);

  return (
    <div data-test="sub-section">
      <FiltersTitle
        onClear={() => handleChange([])}
        title={t('User Agent [ {count} ]', { count: selectedItemsCount })}
        readonly={readonly}
        disabledReasonMessage={disabledReasonMessage}
      />

      {!readonly && (
        <Switch
          switchState={switchState}
          onSwitchChange={onSwitchChange}
          switchOffLabel={t('all')}
          switchOnLabel={t('any')}
        />
      )}

      {optionsEntries.map(([comparisonType, items], index) =>
        items?.length ? (
          <Grid
            key={comparisonType}
            data-test={`section-items-${comparisonLabels[comparisonType].toLowerCase().replaceAll(' ', '-')}`}
          >
            <SelectedItems
              filtersTitle={comparisonLabels[comparisonType]}
              isAddIcon={mapTypeToIconType[comparisonType] === ListIconType.INCLUDE}
              selectedItems={items}
              readonly={readonly}
              handleChange={val => handleChange(val, comparisonType)}
              isRemovingAll={true}
              dataTest={'user-agent'}
            />

            {index < optionsEntries.length - 1 && <DimensionChip targetingDimension={dimension} isInsideOption />}
          </Grid>
        ) : null
      )}
    </div>
  );
};
